/**
 * Contrato de `GET /dispositivos` y de las acciones sobre un equipo.
 * Fuente: `cdr-sa-deploy/docs/specs/2026-10-13-dispositivos-de-tienda.md`, §4.6.
 *
 * Un «dispositivo» es un equipo de tienda (handheld o computadora) que abrió la
 * app nueva. Su id es un UUID al azar que genera la app: no es IMEI, ni número
 * de serie, ni nada del equipo (§6).
 */

/**
 * - `en_pantalla`: algún socket tuvo actividad hace menos de 60 s.
 * - `conexion_abierta`: hay socket, pero sin actividad en ese tiempo (la app
 *   sólo late con la pantalla a la vista: puede estar apagada).
 * - `desconectado`: ningún socket.
 * - `revocado`: TI cerró su sesión; no puede volver a entrar hasta reactivarlo.
 */
export type EstadoDispositivo = 'en_pantalla' | 'conexion_abierta' | 'desconectado' | 'revocado';

/** Lo que el equipo informa después de un «Hacer sonar» (§5.1). */
export type ResultadoSonar = 'sono' | 'bloqueado' | 'sin_audio';

/** Motivo obligatorio al cerrar la sesión de un equipo. */
export type MotivoCierre = 'perdido' | 'robado' | 'baja' | 'otro';

export interface ResumenDispositivos {
  vistos: number;
  enPantalla: number;
  conexionAbierta: number;
  revocados: number;
  nuevos24h: number;
}

export interface CuentaDispositivo {
  id: string;
  email: string;
  /** El rol tal como lo guarda la API: `user.role.branch_office`, etc. */
  rol: string;
}

export interface UltimoVendedor {
  /** `externalUserId` del vendedor elegido en el selector. */
  id: string;
  nombre: string | null;
  en: string | null;
}

export interface UltimoSonar {
  en: string;
  /** Correo de quien lo hizo sonar. */
  por: string | null;
  /** `null` mientras el equipo no confirma (o si nunca llegó a confirmar). */
  resultado: ResultadoSonar | null;
}

/**
 * ⚠️ La spec no fija la forma de `revocado` (sólo da `null` en el ejemplo). Se
 * asume la de las columnas `revocado_*` de la tabla (§8).
 */
export interface RevocacionDispositivo {
  en: string;
  por: string | null;
  motivo: MotivoCierre;
  nota?: string | null;
}

export interface Dispositivo {
  id: string;
  /** Los 4 primeros caracteres del UUID en mayúsculas («7F3A»). */
  codigo: string;
  etiqueta: string | null;
  cuenta: CuentaDispositivo;
  estado: EstadoDispositivo;
  /** Sockets abiertos ahora mismo. */
  sockets: number;
  ultimaActividadWs: string | null;
  primeraVez: string;
  ultimaVez: string;
  /** La primera vez fue hace menos de 24 h. */
  nuevo: boolean;
  ultimoVendedor: UltimoVendedor | null;
  appVersion: string | null;
  navegador: string | null;
  modelo: string | null;
  ultimoSonar: UltimoSonar | null;
  revocado: RevocacionDispositivo | null;
}

export interface ListaDispositivos {
  resumen: ResumenDispositivos;
  dispositivos: Dispositivo[];
  /**
   * ⚠️ FUERA del contrato de §4.6, opcional: cuándo arrancó la API. Tras un
   * reinicio el registro de conexiones está vacío hasta que los equipos
   * reconectan, y la pantalla debe decir «la API se reinició hace N s» en vez
   * de «0 en pantalla» (§4.7). Si la API no lo manda, no se avisa.
   */
  apiIniciadaEn?: string | null;
}

/** `POST /dispositivos/:id/sonar` → 202. */
export interface RespuestaSonar {
  /** `false` si el equipo no tenía ningún socket abierto: el aviso no se encola. */
  entregado: boolean;
  sockets: number;
}

/** `POST /dispositivos/:id/cerrar-sesion` → 200. */
export interface RespuestaCierre {
  socketsCerrados: number;
}

export interface PeticionCierre {
  motivo: MotivoCierre;
  /** Hasta 200 caracteres. Se omite si va vacía. */
  nota?: string;
}

/** Ventanas de «vistos en los últimos N días». La API acepta hasta 90. */
export const DIAS_OPCIONES = [7, 30, 90] as const;
export type DiasVentana = (typeof DIAS_OPCIONES)[number];

export const ETIQUETA_MAX = 60;
export const NOTA_MAX = 200;
